import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

function AdminCustomers({ token, onCustomerCreated }) {
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    organization: '',
    password: '',
    email_reminders_enabled: true,
    sms_reminders_enabled: false
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      // Filter out admin users
      setCustomers(Array.isArray(data) ? data.filter(u => !u.is_admin) : []);
    } catch {
      setCustomers([]);
    }
  }, [token]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const resetForm = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      address_line_2: '',
      city: '',
      state: '',
      zip_code: '',
      organization: '',
      password: '',
      email_reminders_enabled: true,
      sms_reminders_enabled: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const url = editingId 
        ? `${API_URL}/api/admin/users/${editingId}`
        : `${API_URL}/api/admin/users`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save customer');

      setMessage(editingId ? 'Customer updated successfully!' : 'Customer created successfully!');
      resetForm();
      fetchCustomers();
      if (onCustomerCreated && !editingId) onCustomerCreated(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (customer) => {
    setForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      address_line_2: customer.address_line_2 || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      organization: customer.organization || '',
      email_reminders_enabled: customer.email_reminders_enabled !== false,
      sms_reminders_enabled: customer.sms_reminders_enabled || false
    });
    setEditingId(customer.id);
    setShowForm(true);
    setMessage('');
    setError('');
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete ${customer.first_name} ${customer.last_name}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setMessage('');
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${customer.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete customer');
      
      setMessage('Customer deleted successfully!');
      fetchCustomers();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCustomers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCustomers.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    const count = selectedIds.length;
    if (!window.confirm(`Are you sure you want to delete ${count} customer${count > 1 ? 's' : ''}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setMessage('');
    setError('');
    
    try {
      let deleted = 0;
      let errors = [];
      
      for (const id of selectedIds) {
        const res = await fetch(`${API_URL}/api/admin/users/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          deleted++;
        } else {
          const data = await res.json();
          errors.push(data.error || 'Unknown error');
        }
      }
      
      if (deleted > 0) {
        setMessage(`${deleted} customer${deleted > 1 ? 's' : ''} deleted successfully!`);
      }
      if (errors.length > 0) {
        setError(`Failed to delete ${errors.length} customer${errors.length > 1 ? 's' : ''}: ${errors[0]}`);
      }
      
      setSelectedIds([]);
      fetchCustomers();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return [fullName, c.first_name, c.last_name, c.email, c.phone, c.organization]
      .filter(Boolean)
      .some(f => f.toLowerCase().includes(term));
  });

  return (
    <div className="admin-customers">
      <div className="customers-header">
        <h2>Manage Customers</h2>
        <button 
          className="add-customer-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          + Add Customer
        </button>
      </div>

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="customer-form-container">
          <h3>{editingId ? 'Edit Customer' : 'Add New Customer'}</h3>
          <form onSubmit={handleSubmit} className="customer-form">
            <div className="form-row">
              <label>
                First Name *
                <input
                  type="text"
                  value={form.first_name}
                  onChange={e => setForm({ ...form, first_name: e.target.value })}
                  required
                />
              </label>
              <label>
                Last Name *
                <input
                  type="text"
                  value={form.last_name}
                  onChange={e => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label>
                Phone
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Organization
                <input
                  type="text"
                  value={form.organization}
                  onChange={e => setForm({ ...form, organization: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Address
                <input
                  type="text"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Address Line 2
                <input
                  type="text"
                  value={form.address_line_2}
                  onChange={e => setForm({ ...form, address_line_2: e.target.value })}
                  placeholder="Suite, Apt, etc."
                />
              </label>
            </div>
            <div className="form-row">
              <label style={{ flex: 2 }}>
                City
                <input
                  type="text"
                  value={form.city}
                  onChange={e => setForm({ ...form, city: e.target.value })}
                />
              </label>
              <label style={{ flex: 1 }}>
                State
                <select
                  value={form.state}
                  onChange={e => setForm({ ...form, state: e.target.value })}
                  style={{ padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '1rem', background: '#fff' }}
                >
                  <option value="">Select</option>
                  <option value="AL">AL</option>
                  <option value="AK">AK</option>
                  <option value="AZ">AZ</option>
                  <option value="AR">AR</option>
                  <option value="CA">CA</option>
                  <option value="CO">CO</option>
                  <option value="CT">CT</option>
                  <option value="DE">DE</option>
                  <option value="FL">FL</option>
                  <option value="GA">GA</option>
                  <option value="HI">HI</option>
                  <option value="ID">ID</option>
                  <option value="IL">IL</option>
                  <option value="IN">IN</option>
                  <option value="IA">IA</option>
                  <option value="KS">KS</option>
                  <option value="KY">KY</option>
                  <option value="LA">LA</option>
                  <option value="ME">ME</option>
                  <option value="MD">MD</option>
                  <option value="MA">MA</option>
                  <option value="MI">MI</option>
                  <option value="MN">MN</option>
                  <option value="MS">MS</option>
                  <option value="MO">MO</option>
                  <option value="MT">MT</option>
                  <option value="NE">NE</option>
                  <option value="NV">NV</option>
                  <option value="NH">NH</option>
                  <option value="NJ">NJ</option>
                  <option value="NM">NM</option>
                  <option value="NY">NY</option>
                  <option value="NC">NC</option>
                  <option value="ND">ND</option>
                  <option value="OH">OH</option>
                  <option value="OK">OK</option>
                  <option value="OR">OR</option>
                  <option value="PA">PA</option>
                  <option value="RI">RI</option>
                  <option value="SC">SC</option>
                  <option value="SD">SD</option>
                  <option value="TN">TN</option>
                  <option value="TX">TX</option>
                  <option value="UT">UT</option>
                  <option value="VT">VT</option>
                  <option value="VA">VA</option>
                  <option value="WA">WA</option>
                  <option value="WV">WV</option>
                  <option value="WI">WI</option>
                  <option value="WY">WY</option>
                </select>
              </label>
              <label style={{ flex: 1 }}>
                Zip Code
                <input
                  type="text"
                  value={form.zip_code}
                  onChange={e => setForm({ ...form, zip_code: e.target.value })}
                />
              </label>
            </div>
            {!editingId && (
              <div className="form-row">
                <label>
                  Temporary Password
                  <input
                    type="text"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Leave blank if not needed"
                  />
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    If set, customer can log in with their username and this password
                  </small>
                </label>
              </div>
            )}
            <div className="form-row checkbox-row" style={{ flexDirection: 'column', gap: '10px' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.email_reminders_enabled}
                  onChange={e => setForm({ ...form, email_reminders_enabled: e.target.checked })}
                />
                <span>Enable email reminders</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.sms_reminders_enabled}
                  onChange={e => setForm({ ...form, sms_reminders_enabled: e.target.checked })}
                  disabled={!form.phone}
                />
                <span>Enable SMS text reminders {!form.phone && '(requires phone)'}</span>
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="save-btn">
                {editingId ? 'Update Customer' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="customers-search">
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {selectedIds.length > 0 && (
          <button 
            className="trash-btn" 
            onClick={handleBulkDelete}
            title={`Delete ${selectedIds.length} selected customer(s)`}
          >
            🗑️ <span className="trash-count">{selectedIds.length}</span>
          </button>
        )}
      </div>

      {filteredCustomers.length === 0 ? (
        <p className="no-data">No customers found</p>
      ) : (
        <table className="customers-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredCustomers.length && filteredCustomers.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Name</th>
              <th>Organization</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Reminders</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map(c => {
              const hasValidEmail = c.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email);
              const hasValidPhone = c.phone && c.phone.replace(/\D/g, '').length >= 10;
              const remindersEnabled = c.email_reminders_enabled !== false;
              return (
                <tr key={c.id} className={selectedIds.includes(c.id) ? 'selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleSelect(c.id)}
                    />
                  </td>
                  <td><strong>{c.first_name} {c.last_name}</strong></td>
                  <td>{c.organization || '-'}</td>
                  <td className={hasValidEmail ? 'valid-indicator' : 'invalid-indicator'}>
                    {hasValidEmail ? '✓' : '✗'}
                  </td>
                  <td className={hasValidPhone ? 'valid-indicator' : 'invalid-indicator'}>
                    {hasValidPhone ? '✓' : '✗'}
                  </td>
                  <td className={remindersEnabled ? 'valid-indicator' : 'invalid-indicator'}>
                    {remindersEnabled ? '✓ On' : '✗ Off'}
                  </td>
                  <td>
                    <button className="view-btn" onClick={() => setViewingCustomer(c)}>
                      View
                    </button>
                    <button className="edit-btn" onClick={() => handleEdit(c)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="customer-note">
        <em>Note: Customers created here can book time through an admin. 
        They can later claim their account by registering with the same email.</em>
      </p>

      {viewingCustomer && (
        <div className="customer-view-modal-overlay" onClick={() => setViewingCustomer(null)}>
          <div className="customer-view-modal" onClick={e => e.stopPropagation()}>
            <div className="customer-view-header">
              <h3>Customer Details</h3>
              <button className="close-btn" onClick={() => setViewingCustomer(null)}>×</button>
            </div>
            <div className="customer-view-content">
              <div className="detail-row">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{viewingCustomer.first_name} {viewingCustomer.last_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Username:</span>
                <span className="detail-value">{viewingCustomer.username || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{viewingCustomer.email || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{viewingCustomer.phone || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Organization:</span>
                <span className="detail-value">{viewingCustomer.organization || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Address:</span>
                <span className="detail-value">
                  {viewingCustomer.address ? (
                    <>
                      {viewingCustomer.address}
                      {viewingCustomer.address_line_2 && <><br />{viewingCustomer.address_line_2}</>}
                      {(viewingCustomer.city || viewingCustomer.state || viewingCustomer.zip_code) && (
                        <><br />{[viewingCustomer.city, viewingCustomer.state].filter(Boolean).join(', ')} {viewingCustomer.zip_code || ''}</>
                      )}
                    </>
                  ) : '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email Reminders:</span>
                <span className={`detail-value ${viewingCustomer.email_reminders_enabled !== false ? 'status-enabled' : 'status-disabled'}`}>
                  {viewingCustomer.email_reminders_enabled !== false ? '✓ Enabled' : '✗ Disabled'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">SMS Reminders:</span>
                <span className={`detail-value ${viewingCustomer.sms_reminders_enabled ? 'status-enabled' : 'status-disabled'}`}>
                  {viewingCustomer.sms_reminders_enabled ? '✓ Enabled' : '✗ Disabled'}
                </span>
              </div>
            </div>
            <div className="customer-view-actions">
              <button className="close-modal-btn" onClick={() => setViewingCustomer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCustomers;
